import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Divider,
  Search,
} from '@makeably/creativex-design-system';
import { getScoreType } from 'components/internal/scores/shared';
import {
  NULL_VALUE,
  emptyState,
  filterProps,
} from 'components/internal/shared';
import FilterTags from 'components/molecules/FilterTags';
import ItemsFilter from 'components/molecules/ItemsFilter';
import ItemsTable from 'components/molecules/ItemsTable';
import { saveFilterQuery } from 'utilities/filtering';
import { getItemSortBy } from 'utilities/item';
import {
  filterItems,
  getValueOptions,
} from 'utilities/itemFilter';
import { searchItems } from 'utilities/itemSearch';
import {
  internalScorePath,
  internalScoreSubscriptionsPath,
} from 'utilities/routes';
import {
  getPage,
  getParams,
  setParam,
} from 'utilities/url';
import styles from './Scores.module.css';

const scoreProps = PropTypes.shape({
  description: PropTypes.string,
  guidelineCount: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  scoreType: PropTypes.string.isRequired,
  subscriptionCount: PropTypes.number.isRequired,
});

const propTypes = {
  filterSelections: filterProps,
  scores: PropTypes.arrayOf(scoreProps).isRequired,
};

const defaultProps = {
  filterSelections: {},
};

const headers = [
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'scoreType',
    label: 'Score Type',
  },
  {
    key: 'guidelineCount',
    label: 'Guidelines',
  },
  {
    key: 'subscriptionCount',
    label: 'Subscriptions',
  },
  {
    key: 'description',
    label: 'Description',
  },
];

const filterDimensions = [
  {
    label: 'Score Type',
    value: 'scoreType',
  },
];

const searchKeys = [
  'id',
  'name',
];

const sortDefault = {
  asc: true,
  key: 'name',
};

function getItems(scores) {
  return scores.map(({
    description,
    guidelineCount,
    id,
    name,
    scoreType,
    subscriptionCount,
  }) => ({
    description: { value: description || NULL_VALUE },
    guidelineCount: { value: guidelineCount },
    id: { value: id },
    name: {
      element: <a href={internalScorePath(id)}>{ name }</a>,
      value: name,
    },
    scoreType: { value: getScoreType(scoreType) },
    subscriptionCount: {
      element: <a href={internalScoreSubscriptionsPath(id)}>{ subscriptionCount }</a>,
      value: subscriptionCount,
    },
  }));
}

function Scores({
  filterSelections: initialFilterSelections,
  scores,
}) {
  const params = getParams(window);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSelections, setFilterSelections] = useState(initialFilterSelections);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(sortDefault);
  const [page, setPage] = useState(getPage(params));

  const items = useMemo(() => (
    getItems(scores)
  ), [scores]);

  const filterOptions = useMemo(() => (
    getValueOptions(filterDimensions, items)
  ), [items]);

  const filteredItems = useMemo(() => (
    filterItems(items, filterSelections)
  ), [items, filterSelections]);

  const searchedItems = useMemo(() => {
    setPage(1);
    return searchItems(filteredItems, search, searchKeys);
  }, [filteredItems, search]);

  const sortedItems = useMemo(() => {
    const byKeyDir = getItemSortBy(sort.key, sort.asc);

    return searchedItems.sort(byKeyDir);
  }, [searchedItems, sort]);

  const handleFilterSelect = async (value) => {
    setPage(1);
    setFilterSelections(value);

    if (Object.keys(value).length > 0) {
      const uuid = await saveFilterQuery(value);
      setParam('filter_uuid', uuid, params, window);
    } else {
      setParam('filter_uuid', '', params, window);
    }
  };

  return (
    <Card className="u-flexColumn u-gap-24">
      <h5>Template Scores</h5>
      <Divider />
      <div className="u-flexColumn u-gap-16">
        <div className="u-flexRow u-justifyBetween u-alignEnd u-gap-16">
          <div className="u-flexRow u-alignCenter u-gap-8">
            <ItemsFilter
              dimensions={filterDimensions}
              isOpen={filterOpen}
              options={filterOptions}
              selections={filterSelections}
              onClose={() => setFilterOpen(false)}
              onOpen={() => setFilterOpen(true)}
              onSelect={handleFilterSelect}
            />
            <FilterTags
              dimensions={filterDimensions}
              selections={filterSelections}
              onClick={() => setFilterOpen(true)}
              onRemove={handleFilterSelect}
            />
          </div>
        </div>
        <div>
          <Search
            placeholder="Search by Name, ID"
            value={search}
            onChange={setSearch}
          />
        </div>
      </div>
      <div className={styles.table}>
        <ItemsTable
          className="u-scrollShadowRight"
          emptyTableContent={emptyState}
          headers={headers}
          items={sortedItems}
          page={page}
          sort={sort}
          onPageChange={setPage}
          onSortChange={setSort}
        />
      </div>
    </Card>
  );
}

Scores.propTypes = propTypes;
Scores.defaultProps = defaultProps;

export default Scores;
